import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  nationalArchivesApi,
  type NationalArchivesSearchParams,
  type NationalArchivesSearchResult,
  type NationalArchivesRecord,
  type NationalArchivesRecordDetails,
} from "../api/nationalArchives";
import Header from "../components/Common/Header";

// Time period options matching National Archives
const TIME_PERIODS = [
  { code: "0", label: "Dates unknown" },
  { code: "1000", label: "1000-1099" },
  { code: "1100", label: "1100-1199" },
  { code: "1200", label: "1200-1299" },
  { code: "1300", label: "1300-1399" },
  { code: "1400", label: "1400-1499" },
  { code: "1500", label: "1500-1599" },
  { code: "1600", label: "1600-1699" },
  { code: "1700", label: "1700-1799" },
  { code: "1800", label: "1800-1899" },
  { code: "1900", label: "1900-1924" },
  { code: "1925", label: "1925-1949" },
  { code: "1950", label: "1950+" },
];

export default function NationalArchives() {
  const { getAccessTokenSilently } = useAuth0();
  const [searchParams] = useSearchParams();

  // Read URL parameters for pre-filled search
  const initialQuery = searchParams.get("q") || "";
  const initialDateFrom = searchParams.get("dateFrom") || "";
  const initialDateTo = searchParams.get("dateTo") || "";
  const hasUrlParams = !!(initialQuery || initialDateFrom || initialDateTo);

  // Basic search
  const [query, setQuery] = useState(initialQuery);

  // Advanced search options
  const [showAdvanced, setShowAdvanced] = useState(hasUrlParams && (!!initialDateFrom || !!initialDateTo));
  const [allWords, setAllWords] = useState("");
  const [exactPhrase, setExactPhrase] = useState("");
  const [anyWords, setAnyWords] = useState("");
  const [excludingWords, setExcludingWords] = useState("");
  const [reference, setReference] = useState("");
  const [dateFrom, setDateFrom] = useState(initialDateFrom);
  const [dateTo, setDateTo] = useState(initialDateTo);
  const [selectedTimePeriods, setSelectedTimePeriods] = useState<string[]>([]);
  const [heldBy, setHeldBy] = useState<"tna" | "other" | "all">("all");

  // Results state
  const [searchResults, setSearchResults] =
    useState<NationalArchivesSearchResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal state
  const [selectedRecord, setSelectedRecord] =
    useState<NationalArchivesRecord | null>(null);
  const [recordDetails, setRecordDetails] =
    useState<NationalArchivesRecordDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Auto-search when URL params are present
  const [hasAutoSearched, setHasAutoSearched] = useState(false);

  useEffect(() => {
    if (hasUrlParams && !hasAutoSearched && !loading && !searchResults) {
      setHasAutoSearched(true);
      handleSearch(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUrlParams, hasAutoSearched]);

  const handleSearch = async (page = 1) => {
    // Validate at least one search term
    if (
      !query.trim() &&
      !allWords.trim() &&
      !exactPhrase.trim() &&
      !anyWords.trim() &&
      !reference.trim()
    ) {
      setError("Please enter at least one search term");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();

      const params: NationalArchivesSearchParams = {
        page,
        pageSize: 20,
      };

      // Basic query
      if (query.trim()) params.query = query.trim();

      // Advanced options
      if (allWords.trim()) params.allWords = allWords.trim();
      if (exactPhrase.trim()) params.exactPhrase = exactPhrase.trim();
      if (anyWords.trim()) params.anyWords = anyWords.trim();
      if (excludingWords.trim()) params.excludingWords = excludingWords.trim();
      if (reference.trim()) params.reference = reference.trim();
      if (dateFrom) params.dateFrom = parseInt(dateFrom);
      if (dateTo) params.dateTo = parseInt(dateTo);
      if (selectedTimePeriods.length > 0)
        params.timePeriods = selectedTimePeriods;
      if (heldBy !== "all") params.heldBy = heldBy;

      const results = await nationalArchivesApi.searchRecords(params, token);
      setSearchResults(results);
      setCurrentPage(page);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to search records"
      );
      setSearchResults(null);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleSearch(1);
    }
  };

  const handleTimePeriodToggle = (code: string) => {
    setSelectedTimePeriods((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const handleClearForm = () => {
    setQuery("");
    setAllWords("");
    setExactPhrase("");
    setAnyWords("");
    setExcludingWords("");
    setReference("");
    setDateFrom("");
    setDateTo("");
    setSelectedTimePeriods([]);
    setHeldBy("all");
  };

  const handleViewRecord = async (record: NationalArchivesRecord) => {
    setSelectedRecord(record);
    setRecordDetails(null);
    setLoadingDetails(true);

    try {
      const token = await getAccessTokenSilently();
      const details = await nationalArchivesApi.getRecordDetails(
        record.id,
        token
      );
      setRecordDetails(details);
    } catch (err) {
      console.error("Failed to load record details:", err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const closeModal = () => {
    setSelectedRecord(null);
    setRecordDetails(null);
  };

  return (
    <div className="min-h-screen bg-vault-50">
      <Header />
      <main className="container mx-auto px-4 py-8">
        {/* Back link */}
        <Link
          to="/dashboard"
          className="inline-flex items-center text-blue-600 hover:underline mb-6"
        >
          ← Back to Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            National Archives Search
          </h1>
          <p className="text-gray-600">
            Search millions of records from The National Archives and over 2,500
            institutions across the UK
          </p>
        </div>

      {/* Search Form */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        {/* Basic Search */}
        <div className="flex gap-4 mb-4">
          <div className="flex-1">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Search for people, places, or topics..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={loading}
            />
          </div>
          <button
            onClick={() => handleSearch(1)}
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            {loading ? "Searching..." : "Search"}
          </button>
        </div>

        {/* Advanced Search Toggle */}
        <button
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium flex items-center gap-1"
        >
          <span>{showAdvanced ? "▼" : "▶"}</span>
          Advanced Search
        </button>

        {/* Advanced Search Panel */}
        {showAdvanced && (
          <div className="mt-4 border-t pt-4 space-y-6">
            {/* Find Words Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Find words
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    All of these words
                  </label>
                  <input
                    type="text"
                    value={allWords}
                    onChange={(e) => setAllWords(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Exact word or phrase
                  </label>
                  <input
                    type="text"
                    value={exactPhrase}
                    onChange={(e) => setExactPhrase(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    Any of these words
                  </label>
                  <input
                    type="text"
                    value={anyWords}
                    onChange={(e) => setAnyWords(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Don't Find Words Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Don&apos;t find words
              </h3>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Excluding these words
                </label>
                <input
                  type="text"
                  value={excludingWords}
                  onChange={(e) => setExcludingWords(e.target.value)}
                  className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Reference Search */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Search for or within references
              </h3>
              <div>
                <label className="block text-sm text-gray-600 mb-1">
                  Reference
                </label>
                <input
                  type="text"
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="e.g., WO 95"
                  className="w-full md:w-1/2 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
              </div>
            </div>

            {/* Date Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Date</h3>
              <div className="flex flex-wrap gap-4 items-end mb-4">
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    From year
                  </label>
                  <input
                    type="number"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    placeholder="yyyy"
                    min="1"
                    max="2100"
                    className="w-24 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-1">
                    To year
                  </label>
                  <input
                    type="number"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    placeholder="yyyy"
                    min="1"
                    max="2100"
                    className="w-24 px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                {TIME_PERIODS.map((period) => (
                  <label
                    key={period.code}
                    className="flex items-center gap-1 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={selectedTimePeriods.includes(period.code)}
                      onChange={() => handleTimePeriodToggle(period.code)}
                      disabled={loading}
                      className="rounded"
                    />
                    <span>{period.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Held By Section */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">
                Held by
              </h3>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="heldBy"
                    checked={heldBy === "all"}
                    onChange={() => setHeldBy("all")}
                    disabled={loading}
                  />
                  <span>Search all</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="heldBy"
                    checked={heldBy === "tna"}
                    onChange={() => setHeldBy("tna")}
                    disabled={loading}
                  />
                  <span>The National Archives</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="heldBy"
                    checked={heldBy === "other"}
                    onChange={() => setHeldBy("other")}
                    disabled={loading}
                  />
                  <span>Other archives</span>
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 pt-4 border-t">
              <button
                onClick={handleClearForm}
                className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                disabled={loading}
              >
                Clear form
              </button>
              <button
                onClick={() => handleSearch(1)}
                disabled={loading}
                className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {loading ? "Searching..." : "Search"}
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            {error}
          </div>
        )}
      </div>

      {/* Search Results */}
      {searchResults && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Search Results
            </h2>
            <p className="text-gray-600">
              Found {searchResults.total.toLocaleString()} result
              {searchResults.total !== 1 ? "s" : ""}
            </p>
          </div>

          {searchResults.results && searchResults.results.length > 0 ? (
            <div className="space-y-4">
              {searchResults.results.map((result, index) => (
                <div
                  key={result.id || index}
                  className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                  onClick={() => handleViewRecord(result)}
                >
                  <h3 className="text-lg font-semibold text-blue-600 mb-2">
                    {result.title || result.reference || "Untitled Record"}
                  </h3>

                  {result.reference && (
                    <p className="text-sm text-gray-500 mb-1">
                      Reference: {result.reference}
                    </p>
                  )}

                  {result.coveringDates && (
                    <p className="text-sm text-gray-500 mb-1">
                      Date: {result.coveringDates}
                    </p>
                  )}

                  {result.heldBy && result.heldBy.length > 0 && (
                    <p className="text-sm text-gray-500 mb-1">
                      Held by: {result.heldBy.join(", ")}
                    </p>
                  )}

                  {result.description && (
                    <p className="text-gray-700 mt-2 line-clamp-2">
                      {result.description}
                    </p>
                  )}

                  <p className="text-blue-600 text-sm mt-2">
                    Click to view details →
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No results found</p>
          )}

          {/* Pagination */}
          {searchResults.total > 20 && (
            <div className="mt-6 flex justify-center gap-2">
              <button
                onClick={() => handleSearch(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="px-4 py-2 text-gray-700">
                Page {currentPage} of {Math.ceil(searchResults.total / 20)}
              </span>
              <button
                onClick={() => handleSearch(currentPage + 1)}
                disabled={
                  currentPage >= Math.ceil(searchResults.total / 20) || loading
                }
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Record Detail Modal */}
      {selectedRecord && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold text-gray-900">
                  {selectedRecord.title ||
                    selectedRecord.reference ||
                    "Record Details"}
                </h2>
                <button
                  onClick={closeModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              {loadingDetails ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : recordDetails ? (
                <div className="space-y-4">
                  {recordDetails.reference && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">
                        Reference
                      </h4>
                      <p className="text-gray-900">{recordDetails.reference}</p>
                    </div>
                  )}

                  {recordDetails.coveringDates && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">
                        Dates
                      </h4>
                      <p className="text-gray-900">
                        {recordDetails.coveringDates}
                      </p>
                    </div>
                  )}

                  {recordDetails.description && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">
                        Description
                      </h4>
                      <p className="text-gray-900">
                        {recordDetails.description}
                      </p>
                    </div>
                  )}

                  {recordDetails.heldBy && recordDetails.heldBy.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">
                        Held by
                      </h4>
                      <p className="text-gray-900">
                        {recordDetails.heldBy.map((h) => h.name).join(", ")}
                      </p>
                    </div>
                  )}

                  {recordDetails.accessConditions && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">
                        Access conditions
                      </h4>
                      <p className="text-gray-900">
                        {recordDetails.accessConditions}
                      </p>
                    </div>
                  )}

                  {recordDetails.administrativeBackground && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-500">
                        Administrative background
                      </h4>
                      <p className="text-gray-900">
                        {recordDetails.administrativeBackground}
                      </p>
                    </div>
                  )}

                  {recordDetails.digitised && (
                    <div className="bg-green-50 border border-green-200 rounded p-3">
                      <p className="text-green-800 text-sm">
                        ✓ Digital copy available
                      </p>
                    </div>
                  )}

                  <div className="flex gap-4 pt-4 border-t">
                    <a
                      href={recordDetails.externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      View on National Archives →
                    </a>
                    <a
                      href={`/national-archives/record/${selectedRecord.id}`}
                      className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                    >
                      Full Details Page
                    </a>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Fallback to basic info if details failed to load */}
                  {selectedRecord.reference && (
                    <p className="text-gray-600">
                      Reference: {selectedRecord.reference}
                    </p>
                  )}
                  {selectedRecord.coveringDates && (
                    <p className="text-gray-600">
                      Dates: {selectedRecord.coveringDates}
                    </p>
                  )}
                  {selectedRecord.description && (
                    <p className="text-gray-600">{selectedRecord.description}</p>
                  )}
                  <div className="pt-4">
                    <a
                      href={`https://discovery.nationalarchives.gov.uk/details/r/${selectedRecord.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline"
                    >
                      View on National Archives website →
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Information Box */}
      {!searchResults && (
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-2">
            About the National Archives Discovery API
          </h3>
          <p className="text-blue-800 mb-2">
            This search connects to The National Archives Discovery catalogue,
            providing access to:
          </p>
          <ul className="list-disc list-inside text-blue-800 space-y-1">
            <li>Millions of historical records and documents</li>
            <li>Records from The National Archives and 2,500+ institutions</li>
            <li>Census records, birth/death certificates, wills, and more</li>
            <li>
              Archives across England, Wales, Scotland, and Northern Ireland
            </li>
          </ul>
          <p className="text-blue-800 mt-4">
            <strong>Tip:</strong> Use the Advanced Search to filter by date,
            reference number, or specific archive.
          </p>
        </div>
      )}
      </main>
    </div>
  );
}
